<template>
  <span><!-- A renderless component that manages chat integrations --></span>
</template>

<script setup>
/**
 * Documentation links:
 * - ProductFruits: https://help.productfruits.com/en/article/custom-chat-widget-integration
 * - HubSpot Chat: https://developers.hubspot.com/docs/api-reference/latest/conversations/chat-configuration/chat-widget-sdk
 */

import * as Sentry from '@sentry/vue'
import { DefaultApolloClient } from '@vue/apollo-composable'
import { computed, inject, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import featureNames from '@/lib/featureNames'
import { HS_HIDE_CLASS } from '@/lib/hubspotChat'
import { useSupportWidgetsStore } from '@/stores/supportWidgets'

import HUBSPOT_USER_IDENTIFICATION from '../queries/hubspotUserIdentification.gql'

const apolloClient = inject(DefaultApolloClient)
const $features = inject('features')
const { markLiveChatReady } = useSupportWidgetsStore()

const route = useRoute()

// Reactive state
const chatInitialized = ref(false)
const productFruitsChatSetup = ref(false)
const productFruitsButtonListenerRegistered = ref(false)

// Properties
const askAiId = import.meta.env.VITE_APP_ASKAI_ID || null

const hubspotChatFeature = computed(() => $features.feature(featureNames.HUBSPOT_CHAT))

const productFruitsFeature = computed(() => $features.feature(featureNames.PRODUCTFRUITS))

const isAskAiChatEnabled = Boolean(askAiId)

const isHubspotChatEnabled = computed(() => hubspotChatFeature.value?.isActive)

const isLifeRingEnabled = computed(
  () => productFruitsFeature.value?.isActive && productFruitsFeature.value?.config?.showLifeRing
)

// HubSpot
const handleHubspotLoaded = () => {
  markLiveChatReady()

  if (isLifeRingEnabled.value) {
    setHubspotContainerVisible(false)
  }
}

const subscribeHubspotLoaded = () => {
  const conversations = window.HubSpotConversations

  if (!conversations) {
    return
  }

  conversations.off('widgetLoaded', handleHubspotLoaded)
  conversations.on('widgetLoaded', handleHubspotLoaded)

  if (conversations.widget?.status()?.loaded) {
    handleHubspotLoaded()
  }
}

const onHubspotReady = () => {
  subscribeHubspotLoaded()
  loadOrRefreshHubSpotWidget()

  if (isLifeRingEnabled.value && !productFruitsChatSetup.value) {
    setupHubspotChatForProductFruits()
    productFruitsChatSetup.value = true
  }
}

const loadHubspotChat = async () => {
  try {
    const config = hubspotChatFeature.value?.config

    if (!config) {
      throw new Error('HubSpot chat feature config is missing')
    }

    const { scriptSrc, scriptId } = config

    if (!scriptSrc) {
      throw new Error('HubSpot chat script source is missing')
    }

    // HubSpot SDK already exists.
    if (window.HubSpotConversations) {
      await window.HubSpotConversations.clear({
        resetWidget: true,
      })

      // clear({ resetWidget: true }) creates a replacement widget,
      // so HubSpot-specific handlers must be registered again.
      productFruitsChatSetup.value = false

      // Configure identification before loading the replacement widget.
      await identifyHubspotUser()

      subscribeHubspotLoaded()
      window.HubSpotConversations.widget.load()

      if (isLifeRingEnabled.value) {
        setupHubspotChatForProductFruits()
        productFruitsChatSetup.value = true
      }

      return
    }

    // Initial load: configure identification before loading HubSpot.
    await identifyHubspotUser()

    window.hsConversationsOnReady = window.hsConversationsOnReady || []
    window.hsConversationsOnReady.push(onHubspotReady)

    // Avoid loading the HubSpot script more than once.
    if (scriptId && document.getElementById(scriptId)) {
      return
    }

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.defer = true
    script.src = scriptSrc

    if (scriptId) {
      script.id = scriptId
    }

    const firstScript = document.getElementsByTagName('script')[0]

    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript)
    } else {
      document.head.appendChild(script)
    }
  } catch (error) {
    Sentry.captureException(error, {
      level: 'error',
      tags: {
        component: 'ChatManager',
        action: 'loadHubspotChat',
      },
    })
  }
}

const loadOrRefreshHubSpotWidget = () => {
  const widget = window.HubSpotConversations?.widget

  if (!widget) {
    return
  }

  const status = widget.status()

  if (status.loaded) {
    widget.refresh()
  } else {
    widget.load()
  }
}

/**
 * Must only be called after HubSpot Conversations is ready.
 */
const setupHubspotChatForProductFruits = () => {
  if (!productFruitsButtonListenerRegistered.value) {
    window.addEventListener('productfruits_button_ext_widget_init', openHubspotChat)

    productFruitsButtonListenerRegistered.value = true
  }

  window.HubSpotConversations?.off('widgetClosed', handleHubspotChatClosed)
  window.HubSpotConversations?.on('widgetClosed', handleHubspotChatClosed)

  // ProductFruits is the visible support entry point.
  closeHubspotChat()
  setHubspotContainerVisible(false)
}

// The class-based approach means the CSS rule below (`html.hs-widget-hidden
// #hubspot-messages-iframe-container`) is already in place before HubSpot's
// script ever inserts the container, so there's no DOM-timing race to lose —
// no MutationObserver needed to "catch up" with an element that isn't there yet.
const setHubspotContainerVisible = (visible) => {
  document.documentElement.classList.toggle(HS_HIDE_CLASS, !visible)
}

const openHubspotChat = () => {
  setHubspotContainerVisible(true)
  window.HubSpotConversations?.widget?.open()
}

const closeHubspotChat = () => {
  window.HubSpotConversations?.widget?.close()

  if (isLifeRingEnabled.value) {
    setHubspotContainerVisible(false)
  }
}

const handleHubspotChatClosed = () => {
  setHubspotContainerVisible(false)
  showProductFruitsLifeRing()
}

// ProductFruits
const showProductFruitsLifeRing = () => {
  if (isLifeRingEnabled.value) {
    window.productFruits?.api?.inAppCenter?.show()
  }
}

// AskAI
const loadAskAiChat = () => {
  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.id = askAiId
  script.defer = true
  script.src = `https://myaskai.com/api/1.1/wf/embed-v2?i=${askAiId}`

  const firstScript = document.getElementsByTagName('script')[0]

  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  } else {
    document.head.appendChild(script)
  }
}

const identifyHubspotUser = async () => {
  window.hsConversationsSettings = {
    ...window.hsConversationsSettings,
    loadImmediately: false,
  }

  // Never reuse the identity of a previous visitor.
  delete window.hsConversationsSettings.identificationEmail
  delete window.hsConversationsSettings.identificationToken

  try {
    if (!apolloClient) {
      throw new Error('Apollo client is not available')
    }

    const { data } = await apolloClient.query({
      query: HUBSPOT_USER_IDENTIFICATION,
      fetchPolicy: 'network-only',
    })

    const user = data?.hubspotUserIdentification

    if (!user) {
      return
    }

    window.hsConversationsSettings.identificationEmail = user.email
    window.hsConversationsSettings.identificationToken = user.token
  } catch (error) {
    Sentry.captureException(error, {
      level: 'error',
      tags: {
        component: 'ChatManager',
        action: 'identifyHubspotUser',
      },
    })
  }
}

// Initialization
const initializeChatWidgets = () => {
  // Set this before HubSpot's script (and thus the container) ever exist in
  // the DOM, so the CSS rule is already in place the instant the container
  // is inserted — no race with when handleHubspotLoaded happens to run.
  if (isHubspotChatEnabled.value && isLifeRingEnabled.value) {
    document.documentElement.classList.add(HS_HIDE_CLASS)
  }

  if (isHubspotChatEnabled.value) {
    loadHubspotChat()
  }

  if (isAskAiChatEnabled) {
    loadAskAiChat()
  }

  chatInitialized.value = true
}

watch(
  () => route.fullPath,
  async () => {
    if (!isHubspotChatEnabled.value) {
      return
    }

    await nextTick()
    loadOrRefreshHubSpotWidget()
  }
)

watch(
  () => $features.loaded,
  (isLoaded) => {
    if (isLoaded && !chatInitialized.value) {
      initializeChatWidgets()
    }
  },
  { immediate: true }
)

// Cleanup
onBeforeUnmount(() => {
  document.documentElement.classList.remove(HS_HIDE_CLASS)

  if (productFruitsButtonListenerRegistered.value) {
    window.removeEventListener('productfruits_button_ext_widget_init', openHubspotChat)

    productFruitsButtonListenerRegistered.value = false
  }

  if (Array.isArray(window.hsConversationsOnReady)) {
    window.hsConversationsOnReady = window.hsConversationsOnReady.filter(
      (callback) => callback !== onHubspotReady
    )
  }

  if (window.HubSpotConversations) {
    window.HubSpotConversations.off('widgetLoaded', handleHubspotLoaded)

    if (productFruitsChatSetup.value) {
      window.HubSpotConversations.off('widgetClosed', handleHubspotChatClosed)
    }
  }
})
</script>

<style>
/* global stylesheet, not scoped to this component */
html.hs-widget-hidden #hubspot-messages-iframe-container {
  display: none !important;
}
</style>
